import React, { useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowRight, ChevronUp, Layers, Sparkles } from "lucide-react";
import { Link } from "react-router";
import {
  INDUSTRIES,
  getItemsByIndustry,
  getPartnerForSubcategory,
} from "../../data/categoryData";

/**
 * Interactive category → subcategory list with sponsor logos; sponsor opens the same subcategory topic page.
 */
const CategoryPartnerHub = () => {
  const [activeId, setActiveId] = useState(INDUSTRIES[0]?.id ?? "legal");
  const categoriesGridRef = useRef(null);
  const subcategoriesPanelRef = useRef(null);

  const scrollToSubcategories = () => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        subcategoriesPanelRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    });
  };

  const scrollToCategories = () => {
    categoriesGridRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleSelectCategory = (industryId) => {
    setActiveId(industryId);
    scrollToSubcategories();
  };

  const activeIndustry = useMemo(
    () => INDUSTRIES.find((i) => i.id === activeId) ?? INDUSTRIES[0],
    [activeId]
  );

  const items = useMemo(() => getItemsByIndustry(activeIndustry.id), [activeIndustry.id]);

  return (
    <section className="relative overflow-hidden bg-[#f7f4ec] px-4 py-20 text-black sm:px-6 lg:px-8">
      <div
        className="pointer-events-none absolute right-0 top-0 h-64 w-64 rounded-full bg-[#FFD221]/12 blur-3xl"
        aria-hidden
      />
      <div className="relative z-10 mx-auto max-w-8xl">
        <div className="flex flex-col gap-6 justify-between mb-8 lg:mb-12 lg:flex-row lg:items-end">
          <div className="max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-[#FFD221]/35 bg-[#FFD221]/15 px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.2em] text-[#9a7b00]">
              <Sparkles size={14} strokeWidth={2.5} className="text-[#c9a000]" aria-hidden />
              Category mini-sites
            </span>
            <h2 className="mt-4 text-[clamp(2rem,4vw+0.75rem,3.25rem)] font-black leading-[1.05] tracking-tight">
              Six focused ecosystems, one unified lead engine.
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-gray-600 sm:text-base">
              Pick a category to reveal every subcategory. Each row lists the topic and its verified sponsor — open the
              topic title, &ldquo;Explore topic&rdquo;, or the sponsor tile to go to that subcategory page.
            </p>
          </div>
          <Link
            to="/partners#how-to-become-a-partner"
            className="inline-flex shrink-0 items-center gap-2 rounded-xl bg-[#FFD221] px-6 py-3 text-sm font-black text-black shadow-sm transition hover:bg-[#ffdf44]"
          >
            Onboard as provider
            <ArrowRight size={16} strokeWidth={2.5} />
          </Link>
        </div>

        {/* Category selector */}
        <div ref={categoriesGridRef} className="grid gap-3 scroll-mt-28 sm:grid-cols-2 xl:grid-cols-3">
          {INDUSTRIES.map((industry) => {
            const selected = industry.id === activeId;
            return (
              <motion.button
                key={industry.id}
                type="button"
                onClick={() => handleSelectCategory(industry.id)}
                layout
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.99 }}
                className={`relative overflow-hidden rounded-[1.35rem] border p-5 text-left transition-all sm:p-6 ${
                  selected
                    ? "border-[#FFD221] bg-white shadow-[0_28px_70px_-40px_rgba(255,210,33,0.85),0_20px_50px_-40px_rgba(0,0,0,0.25)] ring-[3px] ring-[#FFD221]/35"
                    : "border-black/10 bg-white/80 hover:border-black/20 hover:bg-white hover:shadow-lg"
                }`}
              >
                {selected ? (
                  <motion.div
                    layoutId="cat-glow"
                    className="pointer-events-none absolute inset-0 bg-gradient-to-br from-[#FFD221]/18 via-transparent to-transparent"
                    transition={{ type: "spring", stiffness: 350, damping: 35 }}
                  />
                ) : null}
                <div className="flex relative gap-3 justify-between items-start">
                  <span
                    className={`rounded-full px-3 py-1 text-[11px] font-black ${
                      selected ? "bg-black text-[#FFD221]" : "bg-black/5 text-gray-700"
                    }`}
                  >
                    {industry.itemCount} topics
                  </span>
                  <Layers
                    size={20}
                    className={selected ? "text-[#c9a000]" : "text-black/20"}
                    strokeWidth={2}
                    aria-hidden
                  />
                </div>
                <h3 className={`relative mt-4 text-lg font-black sm:text-xl ${selected ? "text-black" : "text-gray-900"}`}>
                  {industry.label}
                </h3>
                <p className="relative mt-2 text-xs leading-relaxed text-gray-500 line-clamp-2 sm:text-sm">
                  {industry.description}
                </p>
              </motion.button>
            );
          })}
        </div>

        {/* Active category detail */}
        <div
          ref={subcategoriesPanelRef}
          id="category-partner-subcategories"
          className="relative mt-10 scroll-mt-28"
        >
          <AnimatePresence mode="wait">
            <motion.div
              key={activeIndustry.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden rounded-[2rem] border border-black/10 bg-white shadow-[0_32px_90px_-50px_rgba(0,0,0,0.55)]"
            >
              <div className="relative px-6 py-6 bg-gradient-to-r via-gray-900 border-b border-black/8 from-gray-950 to-gray-950 sm:px-10 sm:py-8">
                <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-transparent via-[#FFD221] to-transparent opacity-95" />
                <div className="flex relative flex-col gap-6 lg:flex-row lg:items-start lg:justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-black uppercase tracking-[0.28em] text-[#FFD221]">Subcategories</p>
                    <h3 className="mt-2 text-2xl font-black text-white sm:text-3xl">{activeIndustry.label}</h3>
                    <p className="mt-2 max-w-2xl text-sm text-white/65">{activeIndustry.description}</p>
                  </div>
                  <div className="flex flex-col gap-3 shrink-0 sm:flex-row sm:items-center sm:gap-3">
                    <button
                      type="button"
                      onClick={scrollToCategories}
                      aria-label="Scroll back to category list"
                      className="inline-flex items-center justify-center gap-1.5 rounded-xl border border-white/25 bg-white/10 px-5 py-2.5 text-xs font-black uppercase tracking-wider text-white backdrop-blur-sm transition hover:bg-white/18 sm:whitespace-nowrap"
                    >
                      <ChevronUp size={16} strokeWidth={2.5} aria-hidden />
                      Back to categories
                    </button>
                    <Link
                      to={`/${activeIndustry.id}`}
                      className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#FFD221] px-5 py-2.5 text-xs font-black text-black shadow-md transition hover:bg-[#ffdf44] sm:whitespace-nowrap"
                    >
                      Open category hub
                      <ArrowRight size={15} strokeWidth={2.5} />
                    </Link>
                  </div>
                </div>
              </div>

              <ul className="divide-y divide-black/[0.06]">
                {items.map((item, index) => {
                  const topicPath = `/${activeIndustry.id}/${item.slug}`;
                  const partner = getPartnerForSubcategory(activeIndustry.id, item.slug);
                  return (
                    <motion.li
                      key={item.slug}
                      initial={{ opacity: 0, x: -12 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: Math.min(index * 0.04, 0.35), duration: 0.35 }}
                      className="group flex flex-col gap-4 p-5 transition-colors hover:bg-[#faf9f5] sm:flex-row sm:items-center sm:gap-6 sm:p-6"
                    >
                      <div className="flex-1 min-w-0">
                        <Link
                          to={topicPath}
                          className="text-base font-black transition text-gray-950 underline-offset-2 group-hover:text-black group-hover:underline"
                        >
                          {item.name}
                        </Link>
                        <p className="mt-1.5 text-sm leading-relaxed text-gray-600">{item.description}</p>
                        <Link
                          to={topicPath}
                          className="mt-3 inline-flex items-center gap-1 text-xs font-black uppercase tracking-wide text-[#c9a000] transition hover:text-black"
                        >
                          Explore topic
                          <ArrowRight size={14} strokeWidth={2.5} />
                        </Link>
                      </div>

                      <div className="flex shrink-0 flex-col items-stretch gap-2 sm:w-[200px] sm:items-end">
                        <span className="text-[10px] font-black uppercase tracking-wider text-gray-400">Sponsor</span>
                        <Link
                          to={topicPath}
                          title={`Open ${item.name} — sponsored by ${partner.name}`}
                          className="flex w-full items-center gap-3 rounded-2xl border border-black/10 bg-white px-4 py-3 shadow-sm ring-1 ring-black/[0.03] transition hover:-translate-y-0.5 hover:border-[#FFD221]/60 hover:shadow-[0_14px_36px_-24px_rgba(255,210,33,0.45)]"
                        >
                          <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl bg-[#f7f4ec] p-2">
                            <img
                              src={partner.logoPath}
                              alt=""
                              className="object-contain w-full max-h-8"
                              loading="lazy"
                            />
                          </div>
                          <div className="flex-1 min-w-0 text-left">
                            <p className="text-xs font-black truncate text-gray-950">{partner.name}</p>
                            <p className="truncate text-[10px] font-semibold text-gray-500">{partner.shortLabel}</p>
                          </div>
                          <ArrowRight size={14} className="flex-shrink-0 text-gray-400" strokeWidth={2.5} aria-hidden />
                        </Link>
                      </div>
                    </motion.li>
                  );
                })}
              </ul>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};

export default CategoryPartnerHub;
